import { storedTitleText } from "./noteTitle"

/**
 * Legacy TipTap JSON notes.
 *
 * Early notes (the old onboarding set among them) were stored as a TipTap JSON
 * document instead of markdown, and some carry that whole document in their
 * `title` column too. These two functions are the read-time projection every
 * client applies to them; nothing is ever written back.
 *
 * Reference implementation for the Swift and Kotlin ports, which run the same
 * `legacyTiptap.fixtures.json`. Change all three or none.
 */

type JsonNode = { type: string; text?: string; content?: JsonNode[]; attrs?: Record<string, unknown> }

function extractTextFromNode(node: JsonNode): string {
  if (node.type === "text") return node.text ?? ""
  // Mention nodes (noteMention, hashtag, etc.) store their display text in attrs.label
  if (node.attrs?.label) return String(node.attrs.label)
  if (!node.content) return ""
  return node.content.map(extractTextFromNode).join("")
}

/** The top-level nodes when `content` is a TipTap `doc`, else `null`. */
function parseLegacyDoc(content: string): JsonNode[] | null {
  if (!content || !content.trimStart().startsWith("{")) return null
  try {
    const doc = JSON.parse(content) as JsonNode
    if (doc.type === "doc" && Array.isArray(doc.content)) return doc.content
  } catch {
    // Not valid JSON: not a legacy doc. Expected, nothing to report.
  }
  return null
}

/**
 * Markdown for note content: a TipTap JSON doc is converted (marks dropped, their
 * text kept), anything else is returned unchanged.
 */
export function legacyTiptapToMarkdown(content: string): string {
  if (!content) return ""
  const nodes = parseLegacyDoc(content)
  if (!nodes) return content

  // A malformed node (null, non-array `content`) throws mid-walk: the whole
  // input is then returned unchanged, as the original web pipeline did.
  try {
    return convertNodes(nodes)
  } catch {
    return content
  }
}

function convertNodes(nodes: JsonNode[]): string {
  return nodes
    .map((node) => {
      const text = extractTextFromNode(node)
      if (node.type === "heading") {
        const level = (node.attrs?.level as number) ?? 1
        return "#".repeat(level) + " " + text
      }
      if (node.type === "bulletList" || node.type === "orderedList") {
        return (node.content ?? [])
          .map((item, i) => {
            const itemText = extractTextFromNode(item)
            return node.type === "orderedList" ? `${i + 1}. ${itemText}` : `- ${itemText}`
          })
          .join("\n")
      }
      if (node.type === "codeBlock") {
        const lang = (node.attrs?.language as string) ?? ""
        return "```" + lang + "\n" + text + "\n```"
      }
      if (node.type === "blockquote") {
        return text
          .split("\n")
          .map((line) => "> " + line)
          .join("\n")
      }
      if (node.type === "horizontalRule") return "---"
      return text
    })
    .join("\n\n")
}

/**
 * The title of a TipTap JSON doc: the first top-level node whose text is
 * non-empty, as `storedTitleText` reads it (node text is literal, not a
 * markdown line, so "1. Intro" keeps its number), or `""` when no node has any. `null` when
 * `raw` is not a TipTap doc, so callers keep their own rule for it.
 */
export function legacyTiptapTitle(raw: string): string | null {
  const nodes = parseLegacyDoc(raw)
  if (!nodes) return null
  try {
    for (const node of nodes) {
      const title = storedTitleText(extractTextFromNode(node))
      if (title) return title
    }
  } catch {
    // Malformed node: treat like any non-doc title.
    return null
  }
  return ""
}

/**
 * A stored note title as displayed: plain text, and never a raw TipTap JSON
 * document (old notes stored the whole doc as their title). Read-time only.
 */
export function noteTitleForDisplay(raw: string): string {
  return legacyTiptapTitle(raw) ?? storedTitleText(raw)
}
